import { all, put, call, select, getContext, takeLatest } from 'redux-saga/effects';

import { LOCAL_STORAGE_SESSION_MARKETPLACE_KEY } from 'utils/Session';
import { Logger } from 'utils/Logger';

import { makeSelectMarketplaceApiKey } from 'reducers/user/selectors';
import { apiKey, profileId, questionnairePrefill, questionnaireMatchesResults } from './types';

import {
  requestApiKeySuccess,
  requestApiKeyFailure,
  requestProfileIdSuccess,
  requestProfileIdFailure,
  requestMatchesResultsDataSuccess,
  requestMatchesResultsDataFailure,
  requestQuestionnairePrefillSuccess,
  requestQuestionnairePrefillFailure,
} from './actions';

const logger = new Logger('Saga:Marketplace');

export function* requestApiKeySaga() {
  const routerService = yield getContext('routerService');
  const authApiService = yield getContext('authApiService');
  const sessionService = yield getContext('sessionService');

  try {
    const response = yield call(
      authApiService.post,
      routerService.generate('api.platform.api_key', {
        scope: 'working-capital',
      }),
    );

    const responseMarketplaceJwt = yield call(
      authApiService.post,
      routerService.generate('api.marketplace.authorize', {
        api_key: response.data.key,
      }),
      {},
      {
        publicRequest: true,
      },
    );

    const marketplaceJwtToken = responseMarketplaceJwt.data.jwt;

    const payload = sessionService.decodeToken(marketplaceJwtToken);

    const session = { accessToken: marketplaceJwtToken, expiresAt: payload.exp };
    sessionService.set(session, LOCAL_STORAGE_SESSION_MARKETPLACE_KEY);

    yield put(requestApiKeySuccess(response.data.key));
  } catch (error) {
    logger.error('Failed to get marketplace API key', error);
    yield put(requestApiKeyFailure(error));
  }
}

export function* requestProfileIdSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const apiKeyValue = yield select(makeSelectMarketplaceApiKey());
    const marketplaceToken = yield call(
      authApiService.post,
      routerService.generate('api.marketplace.authorize', {
        api_key: apiKeyValue,
      }),
      {},
      {
        publicRequest: true,
      },
    );

    const accessToken = marketplaceToken?.data?.jwt;
    const response = yield call(
      authApiService.post,
      routerService.generate('api.marketplace.profile_new'),
      {
        questionnaireId: action.questionnaireId,
      },
      { marketplaceToken: accessToken },
    );

    yield put(requestProfileIdSuccess({ profileId: response.data.uuid, accessToken }));

    return action.promise?.resolve({ profileId: response.data.uuid, accessToken });
  } catch (error) {
    logger.info('Failed to receive profile id', error);
    yield put(requestProfileIdFailure());
  }
}

export function* requestQuestionnairePrefillSaga(action) {
  const { values, profile, questionnaireId } = action.payload;
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const res = yield call(
      authApiService.patch,
      routerService.generate('api.marketplace.profile_submit', {
        profileId: profile.profileId,
        questionnaireId,
      }),
      values,
      { marketplaceToken: profile.accessToken },
    );

    yield put(requestQuestionnairePrefillSuccess(res));

    return action.promise?.resolve(res);
  } catch (error) {
    logger.info('Failed to submit questionnaire', error);
    yield put(requestQuestionnairePrefillFailure(error));

    const promiseError = error?.response?.data;
    const shouldReturnError = promiseError && promiseError?.statusCode < 500;

    return action.promise?.reject(shouldReturnError ? promiseError : {});
  }
}

export function* requestQuestionnaireMatchesResultsSaga(action) {
  const { profile, productId } = action.payload;
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const res = yield call(
      authApiService.get,
      `${routerService.generate('api.marketplace.matches_results', {
        profileId: profile.profileId,
      })}`,
      { marketplaceToken: profile.accessToken },
    );

    yield put(requestMatchesResultsDataSuccess(res?.data, productId));

    return action.promise?.resolve(res);
  } catch (error) {
    logger.info('Failed to submit questionnaire', error);

    const promiseError = error?.response?.data;
    const shouldReturnError = promiseError && promiseError?.statusCode < 500;

    yield put(requestMatchesResultsDataFailure(error, productId));

    return action.promise?.reject(shouldReturnError ? promiseError : {});
  }
}

export default function* marketplaceSaga() {
  return yield all([
    takeLatest(apiKey.request, requestApiKeySaga),
    takeLatest(profileId.request, requestProfileIdSaga),
    takeLatest(questionnairePrefill.request, requestQuestionnairePrefillSaga),
    takeLatest(questionnaireMatchesResults.request, requestQuestionnaireMatchesResultsSaga),
  ]);
}
